import { ResearchTask } from '../../types.ts';
import {
  ApiError,
  fetchWithTimeout,
  parseErrorResponse,
  OfflineQueue,
  taskToServer,
  taskFromServer,
  columnFromServer,
  checklistItemFromServer,
} from './base.ts';

export const tasksApi = {
  getTasks: async (projectId: string): Promise<ResearchTask[]> => {
    try {
      const res = await fetchWithTimeout(`/api/projects/${projectId}/tasks`);
      if (!res.ok) await parseErrorResponse(res);
      const rows = await res.json();
      return rows.map(taskFromServer);
    } catch (error) {
      console.warn('⚠️ Database unavailable, returning empty tasks list:', error);
      return [];
    }
  },

  getOpenTaskCount: async (projectId: string, personId: string): Promise<number> => {
    try {
      const res = await fetchWithTimeout(
        `/api/projects/${projectId}/tasks?personId=${personId}&countOnly=true`
      );
      if (!res.ok) await parseErrorResponse(res);
      const data = await res.json();
      return data.count || 0;
    } catch {
      return 0;
    }
  },

  /**
   * Batch open-task counts per persoon voor het hele project (#765).
   * Retourneert een map { personId: count } voor de personenlijst.
   */
  getTaskCountsByPerson: async (projectId: string): Promise<Record<string, number>> => {
    try {
      const res = await fetchWithTimeout(`/api/projects/${projectId}/tasks/counts?groupBy=person`);
      if (!res.ok) await parseErrorResponse(res);
      return (await res.json()) as Record<string, number>;
    } catch (error) {
      console.warn('⚠️ Failed to load task counts per person:', error);
      return {};
    }
  },

  getArchivedTasks: async (projectId: string): Promise<ResearchTask[]> => {
    try {
      const res = await fetchWithTimeout(`/api/projects/${projectId}/tasks?archived=true`);
      if (!res.ok) await parseErrorResponse(res);
      const rows = await res.json();
      return rows.map(taskFromServer);
    } catch (error) {
      console.warn('⚠️ Database unavailable, returning empty archived tasks list:', error);
      return [];
    }
  },

  saveTask: async (task: Partial<ResearchTask>): Promise<ResearchTask> => {
    const isUpdate = !!task.id;
    const serverPayload = taskToServer(task);

    try {
      if (isUpdate) {
        const res = await fetchWithTimeout(`/api/tasks/${task.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(serverPayload),
        });
        if (!res.ok) await parseErrorResponse(res);
        return taskFromServer(await res.json());
      } else {
        const res = await fetchWithTimeout(`/api/projects/${task.projectId}/tasks`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(serverPayload),
        });
        if (!res.ok) await parseErrorResponse(res);
        return taskFromServer(await res.json());
      }
    } catch (error) {
      // #1481 — de queue is er voor een onbereikbare server, niet voor een
      // server die antwoordt met een fout. Zo'n PUT opnieuw aanbieden levert
      // dezelfde 4xx/5xx op, en zolang hij hier werd opgeslokt keerde de
      // aanroep normaal terug: de planner meldde niets terwijl de wijziging
      // nooit was vastgelegd. Zelfde ingreep als projects.ts:179 (#1361).
      if (error instanceof ApiError) throw error;
      console.warn('⚠️ Database unavailable, queuing operation:', error);
      const tempId = task.id || crypto.randomUUID();
      OfflineQueue.enqueue({
        operation: isUpdate ? 'UPDATE' : 'CREATE',
        endpoint: isUpdate ? `/api/tasks/${task.id}` : `/api/projects/${task.projectId}/tasks`,
        method: isUpdate ? 'PUT' : 'POST',
        // #1481 — hier stond `task`: de client-vorm, met personId/columnId/
        // dueDate. De route leest assigned_person_id/due_date, dus elke replay
        // schreef niets terug. De directe call hierboven stuurt serverPayload;
        // de replay hoort exact hetzelfde te sturen.
        data: serverPayload,
      });
      return {
        ...task,
        id: tempId,
        created_at: task.created_at || new Date().toISOString(),
        status: task.status || 'todo',
        _offline: true,
      } as any;
    }
  },

  deleteTask: async (id: string): Promise<void> => {
    try {
      const res = await fetchWithTimeout(`/api/tasks/${id}`, {
        method: 'DELETE',
      });
      if (!res.ok) await parseErrorResponse(res);
    } catch (error) {
      // #1481 — zie saveTask: een 403/404/500 is geen offline-situatie.
      if (error instanceof ApiError) throw error;
      console.warn('⚠️ Database unavailable, queuing operation:', error);
      OfflineQueue.enqueue({
        operation: 'DELETE',
        endpoint: `/api/tasks/${id}`,
        method: 'DELETE',
        data: {},
      });
    }
  },

  syncTaskPersons: async (taskId: string, personIds: string[]): Promise<void> => {
    const res = await fetchWithTimeout(`/api/tasks/${taskId}/persons`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids: personIds }),
    });
    if (!res.ok) await parseErrorResponse(res);
  },

  syncTaskSources: async (taskId: string, sourceIds: string[]): Promise<void> => {
    const res = await fetchWithTimeout(`/api/tasks/${taskId}/sources`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids: sourceIds }),
    });
    if (!res.ok) await parseErrorResponse(res);
  },

  // =====================================================
  // TASK COLUMNS (KANBAN BOARD)
  // =====================================================

  getTaskColumns: async (projectId: string): Promise<import('../../types.ts').TaskColumn[]> => {
    const res = await fetchWithTimeout(`/api/projects/${projectId}/task-columns`);
    if (!res.ok) await parseErrorResponse(res);
    const rows = await res.json();
    return rows.map(columnFromServer);
  },

  createTaskColumn: async (
    projectId: string,
    data: { title: string; color?: string; position?: number; is_done_column?: boolean }
  ): Promise<import('../../types.ts').TaskColumn> => {
    const res = await fetchWithTimeout(`/api/projects/${projectId}/task-columns`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) await parseErrorResponse(res);
    return columnFromServer(await res.json());
  },

  updateTaskColumn: async (
    id: string,
    data: { title?: string; color?: string; position?: number; is_done_column?: boolean }
  ): Promise<import('../../types.ts').TaskColumn> => {
    const res = await fetchWithTimeout(`/api/task-columns/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) await parseErrorResponse(res);
    return columnFromServer(await res.json());
  },

  reorderTaskColumns: async (
    projectId: string,
    columns: { id: string; position: number }[]
  ): Promise<import('../../types.ts').TaskColumn[]> => {
    const res = await fetchWithTimeout(`/api/projects/${projectId}/task-columns/reorder`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ columns }),
    });
    if (!res.ok) await parseErrorResponse(res);
    const rows = await res.json();
    return rows.map(columnFromServer);
  },

  deleteTaskColumn: async (id: string, moveTasksTo?: string): Promise<void> => {
    const res = await fetchWithTimeout(`/api/task-columns/${id}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ moveTasksTo }),
    });
    if (!res.ok) await parseErrorResponse(res);
  },

  // =====================================================
  // TASK MOVE / ARCHIVE / RESTORE
  // =====================================================

  moveTask: async (
    taskId: string,
    data: { column_id: string; position: number }
  ): Promise<ResearchTask> => {
    const res = await fetchWithTimeout(`/api/tasks/${taskId}/move`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) await parseErrorResponse(res);
    return taskFromServer(await res.json());
  },

  archiveTask: async (taskId: string): Promise<void> => {
    const res = await fetchWithTimeout(`/api/tasks/${taskId}/archive`, { method: 'PUT' });
    if (!res.ok) await parseErrorResponse(res);
  },

  restoreTask: async (taskId: string): Promise<ResearchTask> => {
    const res = await fetchWithTimeout(`/api/tasks/${taskId}/restore`, { method: 'PUT' });
    if (!res.ok) await parseErrorResponse(res);
    return taskFromServer(await res.json());
  },

  // =====================================================
  // TASK CHECKLIST
  // =====================================================

  getChecklist: async (taskId: string): Promise<import('../../types.ts').TaskChecklistItem[]> => {
    const res = await fetchWithTimeout(`/api/tasks/${taskId}/checklist`);
    if (!res.ok) await parseErrorResponse(res);
    const rows = await res.json();
    return rows.map(checklistItemFromServer);
  },

  addChecklistItem: async (
    taskId: string,
    title: string
  ): Promise<import('../../types.ts').TaskChecklistItem> => {
    const res = await fetchWithTimeout(`/api/tasks/${taskId}/checklist`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title }),
    });
    if (!res.ok) await parseErrorResponse(res);
    return checklistItemFromServer(await res.json());
  },

  updateChecklistItem: async (
    id: string,
    data: { title?: string; is_completed?: boolean; position?: number }
  ): Promise<import('../../types.ts').TaskChecklistItem> => {
    const res = await fetchWithTimeout(`/api/checklist-items/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) await parseErrorResponse(res);
    return checklistItemFromServer(await res.json());
  },

  deleteChecklistItem: async (id: string): Promise<void> => {
    const res = await fetchWithTimeout(`/api/checklist-items/${id}`, { method: 'DELETE' });
    if (!res.ok) await parseErrorResponse(res);
  },
};
